import DOMPurify from "isomorphic-dompurify";
import { CollectionPageJsonLd } from "next-seo";
import {
  Article,
  EventStatus,
  GenericSport,
  GetHeadToHeadTopLevelDataQuery,
} from "@/graphql/gql/graphql";
import { SEO } from "../constants";
import { getFormattedDateTime } from "./date";
import { findHeadToHeadGameState } from "./headToHead";
import type {
  Organization,
  SportsEvent,
  WithContext,
  EventStatusType,
  Place,
  SportsOrganization,
  SportsTeam,
} from "schema-dts";
export const publisherMarkup: Organization = {
  "@type": "Organization",
  "@id": SEO.ORGANIZATION_ID,
};

export const installActions = [
  {
    "@type": "InstallAction",
    name: "Install FanDuel Sportsbook App",
    target: {
      "@type": "EntryPoint",
      urlTemplate:
        "https://apps.apple.com/us/app/fanduel-sportsbook-casino/id1413721906",
      actionPlatform: ["http://schema.org/IOSPlatform"],
    },
  },
  {
    "@type": "InstallAction",
    name: "Install FanDuel Sportsbook Android App",
    target: {
      "@type": "EntryPoint",
      urlTemplate:
        "https://play.google.com/store/apps/details?id=com.fanduel.sportsbook",
      actionPlatform: ["http://schema.org/AndroidPlatform"],
    },
  },
];

export const potentialActions = (currentUrl: string) => [
  {
    "@type": "ReadAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: currentUrl,
      actionPlatform: [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/IOSPlatform",
        "http://schema.org/AndroidPlatform",
      ],
    },
  },
  ...installActions,
];

export const hasPartArticles = (articles: Article[] | undefined) =>
  articles
    ? articles.map((article) => ({
        type: "Article",
        name: article.title,
        url: `${SEO.FANDUEL_URL_RESEARCH}/${article.slug}`,
        datePublished: article.firstPublishedAt,
        author: { "@type": "Person", name: article.author.name },
        description: article.description,
      }))
    : [];

interface TopLevelCategoryJsonLdProps {
  sport: Partial<GenericSport>;
  articles?: Article[];
}
export const TopLevelCategoryJsonLd = ({
  sport,
  articles,
}: TopLevelCategoryJsonLdProps) => {
  const url = `${SEO.FANDUEL_URL_RESEARCH}/${sport?.slug}`;
  return (
    <CollectionPageJsonLd
      name={`${sport?.name} Odds, Predictions, Picks, Analysis and Betting Trends | FanDuel Research`}
      description={`Explore a comprehensive collection of ${sport.name} research articles, in-depth analysis, betting trends, and predictions provided by FanDuel Research. Enhance your sports betting experience with expert insights tailored to each game and the season.`}
      url={url}
      publisher={publisherMarkup}
      potentialAction={potentialActions(url)}
      hasPart={hasPartArticles(articles)}
    />
  );
};
interface ProgrammaticCollectionJsonLdProps {
  slug: string;
  sub: string;
  name?: string | null;
  sportName?: string;
  articles?: Article[];
  description?: string | null;
}

export const ProgrammaticCollectionJsonLd = ({
  slug,
  sub,
  name,
  sportName,
  articles,
  description,
}: ProgrammaticCollectionJsonLdProps) => {
  const url = `${SEO.FANDUEL_URL_RESEARCH}/${slug}/${sub}`;
  return (
    <CollectionPageJsonLd
      name={name ?? "Analysis Subcategory | FanDuel Research"}
      description={
        description ??
        `Dive into our expert picks, detailed predictions, and carefully curated parlays for the ${sportName}. Our insights are crafted to help you make more informed betting decisions throughout the ${sportName} season.`
      }
      url={url}
      publisher={publisherMarkup}
      potentialAction={potentialActions(url)}
      hasPart={hasPartArticles(articles)}
    />
  );
};

interface SportsEventJsonLdProps {
  eventData: GetHeadToHeadTopLevelDataQuery["event"];
  gameId: number;
}

export const SportsEventJsonLd = ({
  eventData,
  gameId,
}: SportsEventJsonLdProps) => {
  if (!eventData) return null;

  const homeTeam = eventData.homeTeam?.name;
  const homeTeamSlug = eventData.homeTeam?.shortName?.toLowerCase();
  const awayTeam = eventData.awayTeam?.name;
  const awayTeamSlug = eventData.awayTeam?.shortName?.toLowerCase();
  const startTime = eventData.startTime;

  if (!homeTeam || !awayTeam || !startTime || !homeTeamSlug || !awayTeamSlug) {
    return null;
  }

  const venueName = eventData.venueName || "TBD";
  const eventName = `${awayTeam} vs. ${homeTeam}`;
  const eventUrl = `${SEO.FANDUEL_URL_RESEARCH}/nfl/matchups/${gameId}/${awayTeamSlug}-${homeTeamSlug}`;

  const actualGameState = findHeadToHeadGameState(
    eventData.status as EventStatus | null | undefined,
    eventData.scoreboard?.clock,
    eventData.startTime
  );

  const mapEventStatusToSchemaOrg = (
    gameState: EventStatus
  ): EventStatusType => {
    switch (gameState) {
      case EventStatus.Final:
        // schema.org doesn't have EventCompleted using EventScheduled for completed events
        return "https://schema.org/EventScheduled";
      case EventStatus.Live:
        return "https://schema.org/EventScheduled";
      case EventStatus.Cancelled:
        return "https://schema.org/EventCancelled";
      case EventStatus.Postponed:
        return "https://schema.org/EventPostponed";
      case EventStatus.Scheduled:
      default:
        return "https://schema.org/EventScheduled";
    }
  };

  // schema follows schema.org SportsEvent structure
  // see: https://schema.org/SportsEvent
  // typed using schema-dts package for type safety
  const location: Place = {
    "@type": "Place",
    name: venueName,
  };

  const organizer: SportsOrganization = {
    "@type": "SportsOrganization",
    name: "National Football League",
    url: "https://www.nfl.com",
  };

  const awayTeamSchema: SportsTeam = {
    "@type": "SportsTeam",
    name: awayTeam,
    ...(eventData.awayTeam?.logoSVG && { logo: eventData.awayTeam.logoSVG }),
    ...(eventData.awayTeam?.teamSportsbookUrl && {
      url: eventData.awayTeam.teamSportsbookUrl,
    }),
  };

  const homeTeamSchema: SportsTeam = {
    "@type": "SportsTeam",
    name: homeTeam,
    ...(eventData.homeTeam?.logoSVG && { logo: eventData.homeTeam.logoSVG }),
    ...(eventData.homeTeam?.teamSportsbookUrl && {
      url: eventData.homeTeam.teamSportsbookUrl,
    }),
  };

  const schema: WithContext<SportsEvent> = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: eventName,
    startDate: startTime,
    eventStatus: mapEventStatusToSchemaOrg(actualGameState),
    url: eventUrl,
    location,
    organizer,
    awayTeam: awayTeamSchema,
    homeTeam: homeTeamSchema,
    ...(eventData.broadcastInfo?.network && {
      description: `${eventName} on ${getFormattedDateTime(startTime)}`,
    }),
  };

  const safeSchema = DOMPurify.sanitize(JSON.stringify(schema));

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: safeSchema,
      }}
    />
  );
};
